import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MenuIcon from '@mui/icons-material/Menu';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionButton = motion(Button);
const MotionIconButton = motion(IconButton);
const MotionAppBar = motion(AppBar);

const menuItems = [
  { text: 'Home', path: '/' },
  { text: 'Features', path: '/features' },
  { text: 'Technology', path: '/technology' },
  { text: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <List>
      {menuItems.map((item) => (
        <ListItem
          button
          component={Link}
          to={item.path}
          key={item.text}
          onClick={handleDrawerToggle}
          sx={{
            textAlign: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '2px',
              background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
              transform: location.pathname === item.path ? 'scaleX(1)' : 'scaleX(0)',
              transition: 'transform 0.3s ease',
            },
            '&:hover::after': {
              transform: 'scaleX(1)',
            },
          }}
        >
          <ListItemText 
            primary={item.text}
            sx={{
              '& .MuiTypography-root': {
                fontWeight: location.pathname === item.path ? 600 : 400,
                color: location.pathname === item.path ? '#6C5CE7' : 'inherit',
              },
            }}
          />
        </ListItem>
      ))}
      <ListItem sx={{ justifyContent: 'center', pt: 2 }}>
        <MotionButton
          variant="contained"
          color="primary"
          component={Link}
          to="/login"
          onClick={handleDrawerToggle}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          sx={{
            borderRadius: '50px',
            background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
            boxShadow: '0 3px 10px rgba(108, 92, 231, 0.3)',
            '&:hover': {
              background: 'linear-gradient(90deg, #A29BFE 0%, #6C5CE7 100%)',
            },
          }}
        >
          Get Started
        </MotionButton>
      </ListItem>
    </List>
  );

  return (
    <MotionAppBar
      position="sticky"
      color="default"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <MotionBox
            display="flex"
            alignItems="center"
            component={Link}
            to="/"
            sx={{ textDecoration: 'none' }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <DirectionsCarIcon 
              sx={{ 
                mr: 1, 
                color: '#6C5CE7',
                fontSize: { xs: 24, md: 32 },
              }} 
            />
            <MotionTypography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              NimblePark
            </MotionTypography>
          </MotionBox>

          {isMobile ? (
            <>
              <MotionIconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                sx={{
                  color: '#6C5CE7',
                }}
              >
                <MenuIcon />
              </MotionIconButton>
              <Drawer
                variant="temporary"
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: 240,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              >
                {drawer}
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {menuItems.map((item) => (
                <MotionButton
                  key={item.text}
                  color="inherit"
                  component={Link}
                  to={item.path}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  sx={{
                    position: 'relative',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    color: location.pathname === item.path ? '#6C5CE7' : 'inherit',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -2,
                      left: 0,
                      width: '100%',
                      height: '2px',
                      background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
                      transform: location.pathname === item.path ? 'scaleX(1)' : 'scaleX(0)',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::after': {
                      transform: 'scaleX(1)',
                    },
                  }}
                >
                  {item.text}
                </MotionButton>
              ))}
              <MotionButton
                variant="contained"
                component={Link}
                to="/login"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                sx={{
                  borderRadius: '50px',
                  background: 'linear-gradient(90deg, #6C5CE7 0%, #A29BFE 100%)',
                  boxShadow: '0 3px 10px rgba(108, 92, 231, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #A29BFE 0%, #6C5CE7 100%)',
                  },
                }}
              >
                Get Started
              </MotionButton>
            </Box>
          )}
        </Toolbar>
      </Container>
    </MotionAppBar>
  );
};

export default Navbar; 